/**
 * InspectionsIndex mount point.
 *
 * Thin compatibility export consumed by the Rails ERB mounts:
 *   - `app/views/inspections/index.html.erb` (standalone — passes showHeader + auth props)
 *   - `app/views/buildings/tabs/_inspections.html.erb` (embedded building context)
 *   - `app/views/accounts/menu/_inspections_tab.html.erb` (embedded account context)
 *
 * The module-level QueryClient is stable across re-renders. Each feature mount
 * gets its own isolated cache; the standalone and each embedded context share
 * distinct cacheKey / scope IDs via props.
 *
 * Standalone header and New Inspection action are opt-in via `showHeader=true`
 * and authorization props forwarded from Rails without duplicating security checks
 * in client code. Embedded Building/Account flag-ON mounts also pass showHeader
 * (plus headerSubtitle on the building Inspections tab).
 *
 * Flag-aware: when modernizedInspectionListingsEnabled is false (default), renders
 * the legacy Kendo-based listing. When true, renders the modern React Query listing.
 * The flag prop is stripped before forwarding so neither branch receives it.
 */

/* eslint-disable react/prop-types */
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { InspectionsListing } from './InspectionsListing.tsx';
import LegacyInspectionsIndex from './legacy/LegacyInspectionsIndex';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

export default function InspectionsIndex({ modernizedInspectionListingsEnabled = false, ...props }) {
  if (!modernizedInspectionListingsEnabled) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <LegacyInspectionsIndex {...props} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <InspectionsListing {...props} />
    </QueryClientProvider>
  );
}
